import React, { useState, useContext } from 'react';
import { AuthContext } from '../../App';
import { updateDoc, deleteDoc, doc } from 'firebase/firestore';

function ReportsComplaints({ reports }) {
  const { db, userProfile } = useContext(AuthContext);
  const [reportFilter, setReportFilter] = useState('all');
  const [selectedReport, setSelectedReport] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);

  const handleResolveReport = async (reportId) => {
    try {
      await updateDoc(doc(db, 'reports', reportId), {
        status: 'resolved',
        resolvedAt: new Date(),
        resolvedBy: userProfile.uid
      });
      alert('Report resolved successfully');
      setShowReportModal(false);
    } catch (error) {
      console.error('Error resolving report:', error);
      alert('Failed to resolve report');
    }
  };

  const handleDismissReport = async (reportId) => {
    try {
      await updateDoc(doc(db, 'reports', reportId), {
        status: 'dismissed',
        dismissedAt: new Date(),
        dismissedBy: userProfile.uid
      });
      alert('Report dismissed successfully');
      setShowReportModal(false);
    } catch (error) {
      console.error('Error dismissing report:', error);
      alert('Failed to dismiss report');
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this report?')) return;
    
    try {
      await deleteDoc(doc(db, 'reports', reportId));
      alert('Report deleted successfully');
      setShowReportModal(false);
    } catch (error) {
      console.error('Error deleting report:', error);
      alert('Failed to delete report');
    }
  };

  const getFilteredReports = () => {
    if (reportFilter === 'all') return reports;
    return reports.filter(report => report.status === reportFilter);
  };

  const filteredReports = getFilteredReports();

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-2xl shadow-xl">
        <h2 className="text-2xl font-bold mb-6 text-slate-800">Reports & Complaints</h2>
        
        <div className="mb-6">
          <select
            value={reportFilter}
            onChange={(e) => setReportFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
          >
            <option value="all">All Reports</option>
            <option value="pending">Pending</option>
            <option value="resolved">Resolved</option>
            <option value="dismissed">Dismissed</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Reporter</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Reported User</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Reason</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Date</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredReports.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-8 text-gray-500">
                    No reports found
                  </td>
                </tr>
              ) : (
                filteredReports.map((report) => (
                  <tr key={report.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-gray-700">
                      {report.reporterId?.substring(0, 8)}...
                    </td>
                    <td className="py-3 px-4 text-gray-700">
                      {report.reportedUserId?.substring(0, 8)}...
                    </td>
                    <td className="py-3 px-4 text-gray-700">{report.reason || 'N/A'}</td>
                    <td className="py-3 px-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        report.status === 'resolved' 
                          ? 'bg-green-100 text-green-700'
                          : report.status === 'dismissed'
                          ? 'bg-gray-100 text-gray-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {report.status || 'pending'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-700">
                      {report.createdAt?.toDate?.().toLocaleDateString() || 'N/A'}
                    </td>
                    <td className="py-3 px-4">
                      <button
                        onClick={() => {
                          setSelectedReport(report);
                          setShowReportModal(true);
                        }}
                        className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors text-sm font-semibold"
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showReportModal && selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-screen overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-bold text-slate-800">Report Details</h3>
                <button
                  onClick={() => setShowReportModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div className="bg-gray-50 p-4 rounded-xl space-y-3">
                <div>
                  <p className="text-sm text-gray-600">Report ID</p>
                  <p className="font-semibold text-gray-800">{selectedReport.id}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Reporter</p>
                  <p className="font-semibold text-gray-800">{selectedReport.reporterId}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Reported User</p>
                  <p className="font-semibold text-gray-800">{selectedReport.reportedUserId}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Reason</p>
                  <p className="font-semibold text-gray-800">{selectedReport.reason || 'No reason provided'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Description</p>
                  <p className="text-gray-700">{selectedReport.description || 'No description provided'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                    selectedReport.status === 'resolved' 
                      ? 'bg-green-100 text-green-700'
                      : selectedReport.status === 'dismissed'
                      ? 'bg-gray-100 text-gray-700'
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {selectedReport.status || 'pending'}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Reported On</p>
                  <p className="font-semibold text-gray-800">
                    {selectedReport.createdAt?.toDate?.().toLocaleString() || 'N/A'}
                  </p>
                </div>
              </div>

              {(!selectedReport.status || selectedReport.status === 'pending') && (
                <div className="space-y-3">
                  <h4 className="font-bold text-gray-800">Actions</h4>
                  
                  <button
                    onClick={() => handleResolveReport(selectedReport.id)}
                    className="w-full px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-semibold"
                  >
                    Mark as Resolved
                  </button>
                  
                  <button
                    onClick={() => handleDismissReport(selectedReport.id)}
                    className="w-full px-4 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-semibold"
                  >
                    Dismiss Report
                  </button>
                </div>
              )}

              <div className="pt-4 border-t border-gray-200">
                <button
                  onClick={() => handleDeleteReport(selectedReport.id)}
                  className="w-full px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-semibold"
                >
                  Delete Report
                </button>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200">
              <button
                onClick={() => setShowReportModal(false)}
                className="w-full px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReportsComplaints;